/**
 * PRODUCT-SPECIFICATIONS MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * ExportSpecsDialog — choose WHICH SKUs to export and in WHICH format. Every
 * SKU is listed with a checkbox (all on by default); only checked SKUs are
 * exported. The user then picks Excel (.xlsx) or PDF. Portaled to <body> so it
 * sits above the sticky nav (same fix as ProductSpecModal).
 *
 * `notice` is a one-line warning the caller sets after an export — today, that
 * the document went out with no company logo (backlog 194: we never substitute
 * the Brand Command mark into a customer's spec sheet). The dialog stays open
 * while a notice is showing so the user reads it BEFORE sending the file.
 */

import { useState } from "react";
import { createPortal } from "react-dom";

export default function ExportSpecsDialog({ products, busy, notice, onExport, onClose }) {
  // Selection map: product_id -> bool. Default every SKU on.
  const [selected, setSelected] = useState(() => Object.fromEntries(products.map((p) => [p.id, true])));

  const ids = products.map((p) => p.id);
  const chosen = ids.filter((id) => selected[id]);
  const allOn = chosen.length === ids.length;
  const noneOn = chosen.length === 0;

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const setAll = (val) => setSelected(Object.fromEntries(ids.map((id) => [id, val])));

  return createPortal(
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label="Export product specifications"
        style={{ width: 460, maxWidth: "94vw", maxHeight: "86vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <h2 className="modal-title" style={{ marginBottom: 4 }}>Export Product Specifications</h2>
          <button type="button" className="link" onClick={onClose} aria-label="Close" disabled={busy}>✕</button>
        </div>
        <p className="muted" style={{ margin: "0 0 10px", fontSize: 13 }}>
          Only the <strong>checked</strong> SKUs below will be exported.
        </p>

        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
          <button type="button" className="link" onClick={() => setAll(true)} disabled={busy || allOn}>Select all</button>
          <button type="button" className="link" onClick={() => setAll(false)} disabled={busy || noneOn}>Clear all</button>
          <span className="muted" style={{ marginLeft: "auto", fontSize: 12 }}>{chosen.length} of {ids.length} selected</span>
        </div>

        <div style={{ flex: "1 1 auto", overflowY: "auto", border: "1px solid var(--border)", borderRadius: "var(--r-control)", padding: 6 }}>
          {products.map((p) => (
            <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", cursor: "pointer", fontSize: 14 }}>
              <input type="checkbox" checked={!!selected[p.id]} onChange={() => toggle(p.id)} disabled={busy} />
              <span style={{ fontWeight: 600 }}>{p.name}</span>
              {!p.active && <span className="muted" style={{ fontSize: 12 }}>(inactive)</span>}
            </label>
          ))}
        </div>

        {notice && (
          <p className="warning" role="status" style={{ margin: "10px 0 0", fontSize: 13, lineHeight: 1.35 }}>
            {notice}
          </p>
        )}

        <div className="modal-actions">
          <button type="button" className="secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="secondary" onClick={() => onExport(chosen, "pdf")} disabled={busy || noneOn}>
            {busy ? "Exporting…" : "Export PDF"}
          </button>
          <button className="btn" type="button" onClick={() => onExport(chosen, "excel")} disabled={busy || noneOn}>
            {busy ? "Exporting…" : "Export Excel"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
